import { useSyncExternalStore } from 'react'

/**
 * The console's colour-scheme preference: **System** (the default), **Light** or **Dark**.
 *
 * docs/DECISIONS.md, "The platform console has a theme switch — System, Light or Dark — kept in
 * the browser" (2026-10-15). It is a frontend-only, per-browser preference: one `localStorage` key,
 * one attribute on `<html>`, no request and no server state.
 *
 * **The stylesheet does the painting.** `styles.css` swaps the colour ramp on
 * `:root[data-theme="dark"]`, and on `prefers-color-scheme: dark` unless the attribute says
 * `light`. So this module's whole job is to keep one attribute true, and to tell the two controls
 * what the choice is. In `system` mode the attribute is *absent* and the OS media query decides —
 * which is also why there is no `matchMedia` listener here: an OS change repaints through CSS on its
 * own, and a script that re-derived it would be a second answer to a question the browser already
 * answers.
 *
 * **Storage can throw and must never be the reason the app does not start.** Private windows,
 * blocked site data and some embedded webviews throw on `localStorage` access, on read, or on write
 * (quota). Every touch is inside a `try/catch`; a failed read is `system`, and a failed write still
 * applies the choice for this page view.
 */

export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]

/**
 * The key. `index.html`'s inline script reads this same string (it cannot import it), and
 * `no-flash.test.ts` fails if the two ever differ.
 */
export const THEME_STORAGE_KEY = 't4s-console-theme'

export const DEFAULT_THEME: ThemePreference = 'system'

/** Anything that is not exactly `light` or `dark` is `system`: a stale or hand-edited value never wins. */
export function parseThemePreference(raw: unknown): ThemePreference {
  return raw === 'light' || raw === 'dark' ? raw : DEFAULT_THEME
}

/** The slice of `Storage` this module uses, so a test can hand it one that throws. */
export type ThemeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** `window.localStorage`, or `null` when merely touching it throws (a `SecurityError`). */
function browserStorage(): ThemeStorage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** The stored choice, or `system` for anything missing, malformed or unreadable. Never throws. */
export function readStoredPreference(
  storage: ThemeStorage | null = browserStorage(),
): ThemePreference {
  try {
    return parseThemePreference(storage?.getItem(THEME_STORAGE_KEY))
  } catch {
    return DEFAULT_THEME
  }
}

/**
 * Remembers a choice. `system` removes the key, so the default leaves nothing behind. Never throws;
 * reports whether the write took, which nothing in the app needs but a test can see.
 */
export function writeStoredPreference(
  preference: ThemePreference,
  storage: ThemeStorage | null = browserStorage(),
): boolean {
  try {
    if (storage === null) {
      return false
    }
    if (preference === 'system') {
      storage.removeItem(THEME_STORAGE_KEY)
    } else {
      storage.setItem(THEME_STORAGE_KEY, preference)
    }
    return true
  } catch {
    return false
  }
}

/** Puts the choice on `<html>`: `data-theme="light" | "dark"`, or no attribute for `system`. */
export function applyThemePreference(
  preference: ThemePreference,
  root: Element = document.documentElement,
): void {
  if (preference === 'system') {
    root.removeAttribute('data-theme')
  } else if (root.getAttribute('data-theme') !== preference) {
    // Not rewritten when it already says so: the inline script in `index.html` has normally set it
    // before this runs, and an attribute that is "set" to what it already is still wakes every
    // observer of it.
    root.setAttribute('data-theme', preference)
  }
}

export type ThemeStore = {
  /** The current choice. */
  readonly get: () => ThemePreference
  /** Choose: apply, remember (best effort), tell every subscriber. A repeat choice does nothing. */
  readonly set: (preference: ThemePreference) => void
  /** Re-read the stored choice and apply it, telling subscribers if it changed. */
  readonly reload: () => void
  /**
   * Apply the stored choice and begin following other tabs. Idempotent. Called once from
   * `main.tsx`; `index.html`'s inline script has normally applied it already, and this is what
   * makes the choice stick when that script did not run.
   */
  readonly start: () => void
  /** Subscribe to changes; returns the unsubscribe. */
  readonly subscribe: (listener: () => void) => () => void
}

export type ThemeStoreDeps = {
  readonly storage?: () => ThemeStorage | null
  readonly root?: () => Element
  readonly events?: () => Pick<EventTarget, 'addEventListener' | 'removeEventListener'>
}

/** A store over a storage, a root element and an event target — each injectable, each resolved late. */
export function createThemeStore(deps: ThemeStoreDeps = {}): ThemeStore {
  const storage = deps.storage ?? browserStorage
  const root = deps.root ?? (() => document.documentElement)
  const events = deps.events ?? (() => window)

  const listeners = new Set<() => void>()
  let current: ThemePreference | null = null
  let started = false

  const publish = (next: ThemePreference): void => {
    // Nobody has been told a value yet if none was ever read, so the first read is not a change.
    const changed = current !== null && next !== current
    current = next
    applyThemePreference(next, root())
    if (changed) {
      for (const listener of [...listeners]) {
        listener()
      }
    }
  }

  const get = (): ThemePreference => {
    current ??= readStoredPreference(storage())
    return current
  }

  const set = (preference: ThemePreference): void => {
    if (preference === get()) {
      return
    }
    // The choice is applied whether or not it could be remembered: a browser that refuses
    // storage still gets the theme it asked for, for as long as this page lives.
    writeStoredPreference(preference, storage())
    publish(preference)
  }

  const reload = (): void => {
    publish(readStoredPreference(storage()))
  }

  const onStorage = (event: Event): void => {
    // `key === null` is `localStorage.clear()`, which also forgets the choice.
    const { key } = event as StorageEvent
    if (key === null || key === THEME_STORAGE_KEY) {
      reload()
    }
  }

  const start = (): void => {
    reload()
    if (!started) {
      started = true
      events().addEventListener('storage', onStorage)
    }
  }

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  return { get, set, reload, start, subscribe }
}

/** The one store the console uses. */
export const themeStore: ThemeStore = createThemeStore()

/** The current choice and a setter, from a store (the console's own by default). */
export function useThemePreference(
  store: ThemeStore = themeStore,
): readonly [ThemePreference, (preference: ThemePreference) => void] {
  const preference = useSyncExternalStore(store.subscribe, store.get)
  return [preference, store.set]
}
